import type { Media } from '@prisma/client'
import { useId, useState, useCallback } from 'react'

import { useFileUpload, type FileUploadProgress } from '~/hooks/use-file-upload'

// Extended Media type for preview updates
interface PreviewMedia extends Media {
  originalFileName?: string
}

interface UseMediaUploadOptions {
  shopId: string
  onUploadSuccess?: (uploadedMedia: Media[]) => void
  allowedMediaKinds?: ('image' | 'video')[]
}

export function useMediaUpload({
  shopId,
  onUploadSuccess,
  allowedMediaKinds,
}: UseMediaUploadOptions) {
  const uploadInputId = useId()
  const [previewMedia, setPreviewMedia] = useState<PreviewMedia[]>([])

  const {
    uploadFile,
    getUploadState,
    clearError,
    uploadProgress,
    clearUploadProgress,
    abortFileUpload,
    abortAllUploads,
  } = useFileUpload()

  const { isUploading, error: uploadError } = getUploadState(uploadInputId)

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files) return

      // Abort any in-progress uploads before starting new ones
      abortAllUploads()
      clearUploadProgress()

      // Create preview media items for immediate display
      const files = Array.from(event.target.files)
      const previews: PreviewMedia[] = files.map((file, index) => ({
        id: `temp-${uploadInputId}-${index}`,
        src: URL.createObjectURL(file),
        kind: file.type.startsWith('video/') ? 'video' : 'image',
        shopId,
        createdAt: new Date(),
        updatedAt: new Date(),
        archivedAt: null,
        extraFields: null,
        originalFileName: file.name,
      }))

      setPreviewMedia(previews)

      // Start individual file uploads
      const uploadedMedia = await uploadFile(
        uploadInputId,
        event.target.files,
        {
          fileName: 'media',
        },
        allowedMediaKinds
      )

      // Only call onUploadSuccess if we have successful uploads
      // (uploadFile already filters out aborted uploads)
      if (uploadedMedia.length > 0) {
        onUploadSuccess?.(uploadedMedia)
      }

      // Clean up preview URLs for all previews
      previews.forEach((media) => {
        if (media.src.startsWith('blob:')) {
          URL.revokeObjectURL(media.src)
        }
      })

      // Clear preview media after success callback
      setPreviewMedia([])

      // Clear the input
      event.target.value = ''
    },
    [
      uploadInputId,
      shopId,
      abortAllUploads,
      clearUploadProgress,
      uploadFile,
      onUploadSuccess,
      allowedMediaKinds,
    ]
  )

  const getProgressForMedia = useCallback(
    (media: Media | PreviewMedia): FileUploadProgress | undefined => {
      const previewMedia = media as PreviewMedia
      return uploadProgress.find(
        (progress) => previewMedia.originalFileName === progress.fileName
      )
    },
    [uploadProgress]
  )

  return {
    uploadInputId,
    isUploading,
    uploadError,
    uploadProgress,
    previewMedia,
    handleFileUpload,
    clearError,
    abortFileUpload,
    abortAllUploads,
    getProgressForMedia,
  }
}
